<template>
  <div class="finlit">
    <div class="finlit-hero">
      <div class="container">
        <h1 class="finlit-hero__title">
          {{ $t("financeLiteracy.heroTitle") }}
        </h1>
        <p class="finlit-hero__text">
          {{ $t("financeLiteracy.heroText") }}
        </p>
      </div>
    </div>

    <div class="container">
      <div class="finlit-tabs">
        <button
          v-for="tab in tabs"
          :key="tab"
          :class="{ active: tab === activeTab }"
          @click="setTab(tab)"
        >
          {{ $t("financeLiteracy.tabs." + tab) }}
        </button>
      </div>
      <select
        class="finlit-select"
        :value="activeTab"
        @change="setTab($event.target.value)"
      >
        <option
          v-for="tab in tabs"
          :key="tab"
          :value="tab"
        >
          {{ $t("financeLiteracy.tabs." + tab) }}
        </option>
      </select>
    </div>

    <section
      v-show="activeTab === 'faq'"
      class="finlit-pane finlit-pane--grey"
    >
      <div class="container">
        <h2 class="finlit-pane__title">
          {{ $t("financeLiteracy.faqTitle") }}
        </h2>
        <p class="finlit-pane__text">
          {{ $t("financeLiteracy.faqText") }}
        </p>

        <div
          v-for="group in filledFaqGroups"
          :key="group.key"
          class="finlit-group"
          :class="{ open: openGroups[group.key] }"
        >
          <button
            class="finlit-group__head"
            @click="openGroups[group.key] = !openGroups[group.key]"
          >
            {{ $t(group.label) }}
            <i class="finlit-group__arrow" />
          </button>
          <div
            v-show="openGroups[group.key]"
            class="finlit-group__items"
          >
            <div
              v-for="item in faq[group.key]"
              :key="item.id"
              class="finlit-question"
              :class="{ open: openQuestion === item.id }"
            >
              <button
                class="finlit-question__head"
                @click="toggleQuestion(item.id)"
              >
                <span v-html="item.title" />
                <i class="finlit-question__sign" />
              </button>
              <div
                v-show="openQuestion === item.id"
                class="finlit-question__answer"
                v-html="item.description"
              />
            </div>
          </div>
        </div>

        <i18n-t
          keypath="financeLiteracy.faqNote"
          tag="p"
          class="finlit-note"
        >
          <template #email>
            <a href="mailto:Client.care@cic.kz">Client.care@cic.kz</a>
          </template>
        </i18n-t>
      </div>
    </section>

    <section
      v-show="activeTab === 'materials'"
      class="finlit-pane"
    >
      <div class="container">
        <h2 class="finlit-pane__title">
          {{ $t("financeLiteracy.tabs.materials") }}
        </h2>
        <p class="finlit-pane__text">
          {{ $t("financeLiteracy.materialsText") }}
        </p>

        <div class="finlit-cards">
          <a
            v-for="item in materials"
            :key="item.id"
            class="finlit-card"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div
              v-if="item.image"
              class="finlit-card__logo"
            >
              <img
                :src="cdn + item.image"
                :alt="item.title"
              >
            </div>
            <h3 class="finlit-card__title">{{ item.title }}</h3>
            <span class="finlit-card__go">{{ $t("financeLiteracy.go") }} →</span>
          </a>
        </div>

        <template v-if="broadcasts.length">
          <div class="finlit-subhead">
            <h3>{{ $t("financeLiteracy.broadcasts") }}</h3>
            <div class="finlit-subhead__arrows">
              <button
                aria-label="prev"
                @click="reels.slidePrev()"
              >
                ←
              </button>
              <button
                aria-label="next"
                @click="reels.slideNext()"
              >
                →
              </button>
            </div>
          </div>
          <swiper
            slides-per-view="auto"
            :space-between="18"
            class="finlit-reels"
            @swiper="reels = $event"
          >
            <swiper-slide
              v-for="item in broadcasts"
              :key="item.id"
              class="finlit-reel"
            >
              <a
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div class="finlit-reel__cover">
                  <img
                    v-if="item.image"
                    :src="cdn + item.image"
                    :alt="item.title"
                  >
                  <span
                    v-else
                    class="finlit-reel__play"
                  />
                </div>
                <p
                  v-if="item.title"
                  class="finlit-reel__title"
                >{{ item.title }}</p>
              </a>
            </swiper-slide>
          </swiper>
        </template>
      </div>
    </section>

    <section
      v-show="activeTab === 'program'"
      class="finlit-pane"
    >
      <div class="container">
        <h2 class="finlit-pane__title">
          {{ $t("financeLiteracy.tabs.program") }}
        </h2>
        <p class="finlit-pane__text">
          {{ $t("financeLiteracy.programText") }}
        </p>

        <div
          v-for="item in programCards"
          :key="item.id"
          class="finlit-doc"
        >
          <div class="finlit-doc__icon">
            PDF
          </div>
          <div>
            <h3 class="finlit-doc__title">
              {{ item.title || $t("financeLiteracy.tabs.program") }}
            </h3>
            <a
              class="finlit-doc__button"
              :href="item.doc ? cdn + item.doc : null"
              target="_blank"
              rel="noopener noreferrer"
            >{{ $t("financeLiteracy.more") }} →</a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";


const TABS = ["faq", "materials", "program"];

export default {
  components: { Swiper, SwiperSlide },
  data() {
    return {
      tabs: TABS,
      faq: {},
      faqGroups: [
        { key: "main", label: "buttons.basic" },
        { key: "payment", label: "buttons.pay" },
        { key: "design", label: "buttons.decor" },
        { key: "application", label: "buttons.other" },
      ],
      openGroups: { main: true },
      openQuestion: null,
      materials: [],
      broadcasts: [],
      programs: [],
      reels: null,
    };
  },
  computed: {
    ...mapState(["cdn"]),
    activeTab() {
      const tab = this.$route.hash.slice(1);

      return TABS.includes(tab) ? tab : "faq";
    },
    programCards() {
      return this.programs.length ? this.programs : [{ id: 0, title: null, doc: null }];
    },
    filledFaqGroups() {
      return this.faqGroups.filter((group) => (this.faq[group.key] || []).length);
    },
  },
  created() {
    axios
      .get("V1/faq")
      .then((response) => {
        this.faq = response.data.data;
      })
      .catch(() => {});
    axios
      .get("V1/finance-literacy")
      .then((response) => {
        const data = response.data.data;
        this.materials = data.material;
        this.broadcasts = data.broadcast;
        this.programs = data.program.filter((item) => item.doc);
      })
      .catch(() => {});
  },
  methods: {
    setTab(tab) {
      this.$router.replace({ hash: "#" + tab });
    },
    toggleQuestion(id) {
      this.openQuestion = this.openQuestion === id ? null : id;
    },
  },
};
</script>

<style lang="scss" scoped>
$text: #373737;
$muted: #8a8a8a;
$line: #c4c4c4;
$link: #2f6bff;
$gradient: linear-gradient(90deg, #ff143f 0%, #c600dd 49.97%, #1b27ff 100%);

@mixin tablet {
  @media (max-width: 1024px) {
    @content;
  }
}
@mixin mobile {
  @media (max-width: 700px) {
    @content;
  }
}

.finlit {
  font-family: Roboto, sans-serif;
  color: $text;

  button {
    font-family: inherit;
    cursor: pointer;
  }
}

.finlit-hero {
  background: url(../assets/images/aboutBack.png) no-repeat right center;
  background-size: cover;
  padding: 56px 0;

  &__title {
    display: inline-block;
    margin: 0;
    font-size: 38px;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    background: $gradient;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  &__text {
    margin: 12px 0 0;
    max-width: 560px;
    font-size: 20px;
    font-weight: 300;
    line-height: 1.45;
  }
  @media (max-width: 1350px) {
    &__title {
      font-size: 30px;
    }
    &__text {
      max-width: 460px;
      font-size: 18px;
    }
  }
  @media (max-width: 1200px) {
    &__title {
      font-size: 26px;
    }
  }
  @include tablet {
    &__text {
      max-width: 50%;
      font-size: 16px;
    }
  }
  @include mobile {
    background: url(../assets/images/about-little.png) no-repeat right;
    background-size: cover;
    padding: 32px 0;
    &__title {
      font-size: 19px;
    }
    &__text {
      max-width: 70%;
      font-size: 14px;
    }
  }
}

.finlit-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 32px 0 8px;

  button {
    max-width: 360px;
    min-height: 52px;
    padding: 8px 24px;
    border: 1px solid $line;
    border-radius: 10px;
    background: #fff;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.25;
    color: $text;

    &:hover {
      border-color: #999;
    }
    &.active {
      border-color: transparent;
      background: linear-gradient(#fff, #fff) padding-box, $gradient border-box;
      font-weight: 500;
    }
  }
  @include mobile {
    display: none;
  }
}
.finlit-select {
  display: none;
  width: 100%;
  margin: 20px 0 8px;
  padding: 10px 14px;
  border: 1px solid $line;
  border-radius: 6px;
  background: #fff;
  font-size: 15px;
  font-weight: 500;
  color: $text;

  @include mobile {
    display: block;
  }
}

.finlit-pane {
  padding: 28px 0 64px;

  &--grey {
    margin-top: 16px;
    background: #f4f4f4;
  }
  &__title {
    margin: 0;
    font-size: 28px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  &__text {
    margin: 10px 0 0;
    max-width: 760px;
    font-size: 18px;
    font-weight: 300;
    line-height: 1.5;
  }
  @include mobile {
    padding: 20px 0 40px;
    &__title {
      font-size: 20px;
    }
    &__text {
      font-size: 15px;
    }
  }
}

.finlit-group {
  margin-top: 22px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    border: 0;
    background: none;
    font-size: 16px;
    text-transform: uppercase;
    color: $muted;
  }
  &__arrow {
    padding: 3px;
    border: solid $muted;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    transition: transform 0.2s;
  }
  &.open &__arrow {
    transform: rotate(-135deg);
  }
  &__items {
    margin-top: 8px;
    border-radius: 4px;
    background: #fff;
  }
}

.finlit-question {
  border-bottom: 1px solid #ececec;

  &:last-child {
    border-bottom: 0;
  }
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    width: 100%;
    padding: 22px 24px 22px 72px;
    border: 0;
    background: none;
    text-align: left;
    font-size: 18px;
    font-weight: 300;
    color: $text;

    :deep(p) {
      margin: 0;
    }
  }
  &__sign {
    position: relative;
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e9e9e9;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 13px;
      left: 7px;
      right: 7px;
      height: 2px;
      background: #fff;
    }
    &::after {
      transform: rotate(90deg);
      transition: transform 0.2s;
    }
  }
  &.open &__sign::after {
    transform: rotate(0);
  }
  &__answer {
    padding: 0 90px 22px 72px;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.6;

    :deep(a) {
      color: $link;
    }
  }
  @include mobile {
    &__head {
      padding: 16px;
      font-size: 15px;
    }
    &__answer {
      padding: 0 16px 16px;
      font-size: 14px;
    }
  }
}

.finlit-note {
  margin: 28px 0 0;
  padding: 20px 24px 20px 72px;
  border-radius: 4px;
  background: #fff;
  font-size: 16px;
  font-weight: 300;

  a {
    color: $link;
  }
  @include mobile {
    padding: 16px;
    font-size: 14px;
  }
}

.finlit-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 24px;

  @include tablet {
    grid-template-columns: repeat(2, 1fr);
  }
  @include mobile {
    grid-template-columns: 1fr;
  }
}
.finlit-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-height: 220px;
  padding: 22px 18px 20px;
  border: 1px solid #e6e9f2;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff 0%, #f5f8ff 100%);
  text-align: center;
  text-decoration: none;
  color: $text;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 10px 28px rgba(40, 60, 140, 0.12);
    transform: translateY(-2px);
  }
  &__logo {
    display: grid;
    place-items: center;
    width: 100%;
    height: 84px;

    img {
      max-width: 100%;
      max-height: 78px;
      object-fit: contain;
    }
  }
  &__title {
    display: flex;
    flex: 1;
    align-items: center;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
  }
  &__go {
    font-size: 14px;
    font-weight: 500;
    color: $link;
  }
  @include mobile {
    min-height: 0;
  }
}

.finlit-subhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 44px;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  &__arrows {
    display: flex;
    flex: none;
    gap: 8px;

    button {
      width: 36px;
      height: 36px;
      border: 1px solid $line;
      border-radius: 50%;
      background: #fff;
      font-size: 16px;
      color: $text;
    }
  }
  @include mobile {
    h3 {
      font-size: 16px;
    }
  }
}
.finlit-reels {
  margin-top: 18px;
}
.finlit-reel {
  width: 200px;

  a {
    display: block;
    text-decoration: none;
    color: $text;
  }
  &__cover {
    display: grid;
    place-items: center;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    border-radius: 14px;
    background: $gradient;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  &__play {
    border-style: solid;
    border-width: 16px 0 16px 26px;
    border-color: transparent transparent transparent #fff;
  }
  &__title {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.3;
  }
  @include mobile {
    width: 160px;
  }
}

.finlit-doc {
  display: flex;
  align-items: center;
  gap: 22px;
  max-width: 720px;
  margin-top: 24px;
  padding: 26px 28px;
  border: 1px solid #e6e9f2;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff 0%, #f5f8ff 100%);

  &__icon {
    display: grid;
    flex: none;
    place-items: center;
    width: 64px;
    height: 76px;
    border-radius: 8px;
    background: $gradient;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
  }
  &__title {
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 500;
  }
  &__button {
    display: inline-block;
    padding: 12px 20px;
    border: 1px solid #eee;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    color: #7a2bbf;
    cursor: pointer;

    &:not([href]),
    &:not([href]):hover {
      color: #7a2bbf;
    }
    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    }
  }
  @include mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
